//# sourceMappingURL=spreadsheet.mjs.map
/// <reference types="./spreadsheet.d.mts" />
import * as $json from "../../gleam_json/gleam/json.mjs";
import * as $bit_array from "../../gleam_stdlib/gleam/bit_array.mjs";
import * as $dict from "../../gleam_stdlib/gleam/dict.mjs";
import * as $decode from "../../gleam_stdlib/gleam/dynamic/decode.mjs";
import * as $float from "../../gleam_stdlib/gleam/float.mjs";
import * as $int from "../../gleam_stdlib/gleam/int.mjs";
import * as $list from "../../gleam_stdlib/gleam/list.mjs";
import * as $option from "../../gleam_stdlib/gleam/option.mjs";
import { None, Some } from "../../gleam_stdlib/gleam/option.mjs";
import * as $pair from "../../gleam_stdlib/gleam/pair.mjs";
import * as $result from "../../gleam_stdlib/gleam/result.mjs";
import * as $mime_types from "../../mime_types/mime_types.mjs";
import * as $list_ from "../../tiamat/gleam/list_.mjs";
import * as $cell_graph from "../data/cell_mapping.mjs";
import * as $cell_mapping from "../data/proposal_element/cell_mapping.mjs";
import * as $remote from "../data/remote.mjs";
import * as $language from "../data/translate/language.mjs";
import * as $iso639 from "../data/translate/language/iso639.mjs";
import * as $decrypt from "../decrypt.mjs";
import { Ok, toList, CustomType as $CustomType } from "../gleam.mjs";

/**
 * Contains additional information for AI. Multiple additional information
 * cells can exist for one question cell. Contains data that cannot be
 * classified. The AI will use it to improve the generated response.
 */
export class AdditionalInformationCell extends $CustomType {}
export const CellType$AdditionalInformationCell = () =>
  new AdditionalInformationCell();
export const CellType$isAdditionalInformationCell = (value) =>
  value instanceof AdditionalInformationCell;

/**
 * Contains answer for the question. Every question should have at most one
 * answer. Indicates an empty cell, in which we will write text (i.e. `"long"`).
 */
export class AnswerCell extends $CustomType {}
export const CellType$AnswerCell = () => new AnswerCell();
export const CellType$isAnswerCell = (value) => value instanceof AnswerCell;

/**
 * Contains dropdown. A question can have multiple dropdowns. Each dropdown
 * content will be filled with the predefined answers.
 */
export class MultipleChoiceCell extends $CustomType {}
export const CellType$MultipleChoiceCell = () => new MultipleChoiceCell();
export const CellType$isMultipleChoiceCell = (value) =>
  value instanceof MultipleChoiceCell;

/**
 * Question index, used in outline. `I.1`, `II.12`, etc.
 */
export class NumberingCell extends $CustomType {}
export const CellType$NumberingCell = () => new NumberingCell();
export const CellType$isNumberingCell = (value) =>
  value instanceof NumberingCell;

/**
 * Indicates a row containing a section. A spreadsheet can have an arbitrary
 * amount of sections.
 */
export class SectionCell extends $CustomType {}
export const CellType$SectionCell = () => new SectionCell();
export const CellType$isSectionCell = (value) => value instanceof SectionCell;

/**
 * Contains additional information for AI. Can be present on multiple columns.
 * Contains all data that cannot be classified. The AI will use it to improve
 * the generated response.
 */
export class ContextColumn extends $CustomType {}
export const ColumnType$ContextColumn = () => new ContextColumn();
export const ColumnType$isContextColumn = (value) =>
  value instanceof ContextColumn;

/**
 * Contains answer for the question. Every line should contain at most one
 * answer. Indicates an empty cell, in which we will write text (i.e. `"long"`).
 */
export class AnswerColumn extends $CustomType {}
export const ColumnType$AnswerColumn = () => new AnswerColumn();
export const ColumnType$isAnswerColumn = (value) =>
  value instanceof AnswerColumn;

/**
 * Contains a category label for a question. Can appear on multiple columns.
 */
export class CategoryColumn extends $CustomType {}
export const ColumnType$CategoryColumn = () => new CategoryColumn();
export const ColumnType$isCategoryColumn = (value) =>
  value instanceof CategoryColumn;

/**
 * Contains dropdown. A question can have multiple dropdowns. Each dropdown
 * content will be filled with the predefined answers.
 */
export class MultipleChoiceColumn extends $CustomType {}
export const ColumnType$MultipleChoiceColumn = () => new MultipleChoiceColumn();
export const ColumnType$isMultipleChoiceColumn = (value) =>
  value instanceof MultipleChoiceColumn;

/**
 * Front-only label for a yes/no answer column. Behaves exactly like a
 * `MultipleChoiceColumn` whose choices default to the localized Yes/No/N/A
 * set. Decoded from the backend `"yes_no"` column type; the cell-mapping
 * graph itself only knows multiple-choice answers.
 */
export class YesNoColumn extends $CustomType {}
export const ColumnType$YesNoColumn = () => new YesNoColumn();
export const ColumnType$isYesNoColumn = (value) => value instanceof YesNoColumn;

/**
 * Question index, used in outline. `I.1`, `II.12`, etc.
 */
export class NumberingColumn extends $CustomType {}
export const ColumnType$NumberingColumn = () => new NumberingColumn();
export const ColumnType$isNumberingColumn = (value) =>
  value instanceof NumberingColumn;

/**
 * Contains question. Every line should contain at most one question.
 * Indicates a filled cell, in which we write the text.
 */
export class QuestionColumn extends $CustomType {}
export const ColumnType$QuestionColumn = () => new QuestionColumn();
export const ColumnType$isQuestionColumn = (value) =>
  value instanceof QuestionColumn;

/**
 * Only present in Question Bank spreadsheet ingestion. Question Bank
 * ingestion are made with pairs of #(question, answer), and can contain an
 * optional tag.
 */
export class TagColumn extends $CustomType {}
export const ColumnType$TagColumn = () => new TagColumn();
export const ColumnType$isTagColumn = (value) => value instanceof TagColumn;

/**
 * Column present in the spreadsheet, which will be present during the
 * export, but unused in the AI process.
 */
export class UnknownColumn extends $CustomType {}
export const ColumnType$UnknownColumn = () => new UnknownColumn();
export const ColumnType$isUnknownColumn = (value) =>
  value instanceof UnknownColumn;

/**
 * Indicates a row containing a top-level section header.
 */
export class H1Row extends $CustomType {}
export const RowType$H1Row = () => new H1Row();
export const RowType$isH1Row = (value) => value instanceof H1Row;

/**
 * Indicates a row containing a second-level section header.
 */
export class H2Row extends $CustomType {}
export const RowType$H2Row = () => new H2Row();
export const RowType$isH2Row = (value) => value instanceof H2Row;

/**
 * Indicates a row containing a third-level section header.
 */
export class H3Row extends $CustomType {}
export const RowType$H3Row = () => new H3Row();
export const RowType$isH3Row = (value) => value instanceof H3Row;

/**
 * Indicates a row containing a question. A spreadsheet can have an arbitrary
 * amount of questions.
 */
export class QuestionRow extends $CustomType {}
export const RowType$QuestionRow = () => new QuestionRow();
export const RowType$isQuestionRow = (value) => value instanceof QuestionRow;

/**
 * Indicates a row whose content is unknown. That line will be ignored during
 * the AI process, but will be preserved during the export.
 */
export class UnknownRow extends $CustomType {}
export const RowType$UnknownRow = () => new UnknownRow();
export const RowType$isUnknownRow = (value) => value instanceof UnknownRow;

export class Worksheet extends $CustomType {
  constructor(name, start_index, content, columns_type, column_choices, rows_type, empty_columns, sheet_merged_cells, cell_mapping, col_widths, row_heights, excel_style, advanced_detection) {
    super();
    this.name = name;
    this.start_index = start_index;
    this.content = content;
    this.columns_type = columns_type;
    this.column_choices = column_choices;
    this.rows_type = rows_type;
    this.empty_columns = empty_columns;
    this.sheet_merged_cells = sheet_merged_cells;
    this.cell_mapping = cell_mapping;
    this.col_widths = col_widths;
    this.row_heights = row_heights;
    this.excel_style = excel_style;
    this.advanced_detection = advanced_detection;
  }
}
export const Worksheet$Worksheet = (name, start_index, content, columns_type, column_choices, rows_type, empty_columns, sheet_merged_cells, cell_mapping, col_widths, row_heights, excel_style, advanced_detection) =>
  new Worksheet(name,
  start_index,
  content,
  columns_type,
  column_choices,
  rows_type,
  empty_columns,
  sheet_merged_cells,
  cell_mapping,
  col_widths,
  row_heights,
  excel_style,
  advanced_detection);
export const Worksheet$isWorksheet = (value) => value instanceof Worksheet;
export const Worksheet$Worksheet$name = (value) => value.name;
export const Worksheet$Worksheet$0 = (value) => value.name;
export const Worksheet$Worksheet$start_index = (value) => value.start_index;
export const Worksheet$Worksheet$1 = (value) => value.start_index;
export const Worksheet$Worksheet$content = (value) => value.content;
export const Worksheet$Worksheet$2 = (value) => value.content;
export const Worksheet$Worksheet$columns_type = (value) => value.columns_type;
export const Worksheet$Worksheet$3 = (value) => value.columns_type;
export const Worksheet$Worksheet$column_choices = (value) =>
  value.column_choices;
export const Worksheet$Worksheet$4 = (value) => value.column_choices;
export const Worksheet$Worksheet$rows_type = (value) => value.rows_type;
export const Worksheet$Worksheet$5 = (value) => value.rows_type;
export const Worksheet$Worksheet$empty_columns = (value) => value.empty_columns;
export const Worksheet$Worksheet$6 = (value) => value.empty_columns;
export const Worksheet$Worksheet$sheet_merged_cells = (value) =>
  value.sheet_merged_cells;
export const Worksheet$Worksheet$7 = (value) => value.sheet_merged_cells;
export const Worksheet$Worksheet$cell_mapping = (value) => value.cell_mapping;
export const Worksheet$Worksheet$8 = (value) => value.cell_mapping;
export const Worksheet$Worksheet$col_widths = (value) => value.col_widths;
export const Worksheet$Worksheet$9 = (value) => value.col_widths;
export const Worksheet$Worksheet$row_heights = (value) => value.row_heights;
export const Worksheet$Worksheet$10 = (value) => value.row_heights;
export const Worksheet$Worksheet$excel_style = (value) => value.excel_style;
export const Worksheet$Worksheet$11 = (value) => value.excel_style;
export const Worksheet$Worksheet$advanced_detection = (value) =>
  value.advanced_detection;
export const Worksheet$Worksheet$12 = (value) => value.advanced_detection;

export class LegacyWorksheet extends $CustomType {
  constructor(name, start_index, content, columns_type, column_choices, rows_type, empty_columns, sheet_merged_cells, col_widths, row_heights, excel_style) {
    super();
    this.name = name;
    this.start_index = start_index;
    this.content = content;
    this.columns_type = columns_type;
    this.column_choices = column_choices;
    this.rows_type = rows_type;
    this.empty_columns = empty_columns;
    this.sheet_merged_cells = sheet_merged_cells;
    this.col_widths = col_widths;
    this.row_heights = row_heights;
    this.excel_style = excel_style;
  }
}
export const Worksheet$LegacyWorksheet = (name, start_index, content, columns_type, column_choices, rows_type, empty_columns, sheet_merged_cells, col_widths, row_heights, excel_style) =>
  new LegacyWorksheet(name,
  start_index,
  content,
  columns_type,
  column_choices,
  rows_type,
  empty_columns,
  sheet_merged_cells,
  col_widths,
  row_heights,
  excel_style);
export const Worksheet$isLegacyWorksheet = (value) =>
  value instanceof LegacyWorksheet;
export const Worksheet$LegacyWorksheet$name = (value) => value.name;
export const Worksheet$LegacyWorksheet$0 = (value) => value.name;
export const Worksheet$LegacyWorksheet$start_index = (value) =>
  value.start_index;
export const Worksheet$LegacyWorksheet$1 = (value) => value.start_index;
export const Worksheet$LegacyWorksheet$content = (value) => value.content;
export const Worksheet$LegacyWorksheet$2 = (value) => value.content;
export const Worksheet$LegacyWorksheet$columns_type = (value) =>
  value.columns_type;
export const Worksheet$LegacyWorksheet$3 = (value) => value.columns_type;
export const Worksheet$LegacyWorksheet$column_choices = (value) =>
  value.column_choices;
export const Worksheet$LegacyWorksheet$4 = (value) => value.column_choices;
export const Worksheet$LegacyWorksheet$rows_type = (value) => value.rows_type;
export const Worksheet$LegacyWorksheet$5 = (value) => value.rows_type;
export const Worksheet$LegacyWorksheet$empty_columns = (value) =>
  value.empty_columns;
export const Worksheet$LegacyWorksheet$6 = (value) => value.empty_columns;
export const Worksheet$LegacyWorksheet$sheet_merged_cells = (value) =>
  value.sheet_merged_cells;
export const Worksheet$LegacyWorksheet$7 = (value) => value.sheet_merged_cells;
export const Worksheet$LegacyWorksheet$col_widths = (value) => value.col_widths;
export const Worksheet$LegacyWorksheet$8 = (value) => value.col_widths;
export const Worksheet$LegacyWorksheet$row_heights = (value) =>
  value.row_heights;
export const Worksheet$LegacyWorksheet$9 = (value) => value.row_heights;
export const Worksheet$LegacyWorksheet$excel_style = (value) =>
  value.excel_style;
export const Worksheet$LegacyWorksheet$10 = (value) => value.excel_style;

export const Worksheet$column_choices = (value) => value.column_choices;
export const Worksheet$columns_type = (value) => value.columns_type;
export const Worksheet$content = (value) => value.content;
export const Worksheet$empty_columns = (value) => value.empty_columns;
export const Worksheet$name = (value) => value.name;
export const Worksheet$rows_type = (value) => value.rows_type;
export const Worksheet$sheet_merged_cells = (value) => value.sheet_merged_cells;
export const Worksheet$start_index = (value) => value.start_index;

export class CellMergeSpan extends $CustomType {
  constructor(rowspan, colspan) {
    super();
    this.rowspan = rowspan;
    this.colspan = colspan;
  }
}
export const CellMergeSpan$CellMergeSpan = (rowspan, colspan) =>
  new CellMergeSpan(rowspan, colspan);
export const CellMergeSpan$isCellMergeSpan = (value) =>
  value instanceof CellMergeSpan;
export const CellMergeSpan$CellMergeSpan$rowspan = (value) => value.rowspan;
export const CellMergeSpan$CellMergeSpan$0 = (value) => value.rowspan;
export const CellMergeSpan$CellMergeSpan$colspan = (value) => value.colspan;
export const CellMergeSpan$CellMergeSpan$1 = (value) => value.colspan;

export class CellCoordinates extends $CustomType {
  constructor(row, column) {
    super();
    this.row = row;
    this.column = column;
  }
}
export const CellCoordinates$CellCoordinates = (row, column) =>
  new CellCoordinates(row, column);
export const CellCoordinates$isCellCoordinates = (value) =>
  value instanceof CellCoordinates;
export const CellCoordinates$CellCoordinates$row = (value) => value.row;
export const CellCoordinates$CellCoordinates$0 = (value) => value.row;
export const CellCoordinates$CellCoordinates$column = (value) => value.column;
export const CellCoordinates$CellCoordinates$1 = (value) => value.column;

export class Cell extends $CustomType {
  constructor(value, original_coordinates, dropdown, color, inline_styles) {
    super();
    this.value = value;
    this.original_coordinates = original_coordinates;
    this.dropdown = dropdown;
    this.color = color;
    this.inline_styles = inline_styles;
  }
}
export const Cell$Cell = (value, original_coordinates, dropdown, color, inline_styles) =>
  new Cell(value, original_coordinates, dropdown, color, inline_styles);
export const Cell$isCell = (value) => value instanceof Cell;
export const Cell$Cell$value = (value) => value.value;
export const Cell$Cell$0 = (value) => value.value;
export const Cell$Cell$original_coordinates = (value) =>
  value.original_coordinates;
export const Cell$Cell$1 = (value) => value.original_coordinates;
export const Cell$Cell$dropdown = (value) => value.dropdown;
export const Cell$Cell$2 = (value) => value.dropdown;
export const Cell$Cell$color = (value) => value.color;
export const Cell$Cell$3 = (value) => value.color;
export const Cell$Cell$inline_styles = (value) => value.inline_styles;
export const Cell$Cell$4 = (value) => value.inline_styles;

export class Spreadsheet extends $CustomType {
  constructor(id, name, type_, data, language, blob, worksheets_key, worksheets_manifest) {
    super();
    this.id = id;
    this.name = name;
    this.type_ = type_;
    this.data = data;
    this.language = language;
    this.blob = blob;
    this.worksheets_key = worksheets_key;
    this.worksheets_manifest = worksheets_manifest;
  }
}
export const Spreadsheet$Spreadsheet = (id, name, type_, data, language, blob, worksheets_key, worksheets_manifest) =>
  new Spreadsheet(id,
  name,
  type_,
  data,
  language,
  blob,
  worksheets_key,
  worksheets_manifest);
export const Spreadsheet$isSpreadsheet = (value) =>
  value instanceof Spreadsheet;
export const Spreadsheet$Spreadsheet$id = (value) => value.id;
export const Spreadsheet$Spreadsheet$0 = (value) => value.id;
export const Spreadsheet$Spreadsheet$name = (value) => value.name;
export const Spreadsheet$Spreadsheet$1 = (value) => value.name;
export const Spreadsheet$Spreadsheet$type_ = (value) => value.type_;
export const Spreadsheet$Spreadsheet$2 = (value) => value.type_;
export const Spreadsheet$Spreadsheet$data = (value) => value.data;
export const Spreadsheet$Spreadsheet$3 = (value) => value.data;
export const Spreadsheet$Spreadsheet$language = (value) => value.language;
export const Spreadsheet$Spreadsheet$4 = (value) => value.language;
export const Spreadsheet$Spreadsheet$blob = (value) => value.blob;
export const Spreadsheet$Spreadsheet$5 = (value) => value.blob;
export const Spreadsheet$Spreadsheet$worksheets_key = (value) =>
  value.worksheets_key;
export const Spreadsheet$Spreadsheet$6 = (value) => value.worksheets_key;
export const Spreadsheet$Spreadsheet$worksheets_manifest = (value) =>
  value.worksheets_manifest;
export const Spreadsheet$Spreadsheet$7 = (value) => value.worksheets_manifest;

export class WorksheetSummary extends $CustomType {
  constructor(name, index, start_index, rows, columns) {
    super();
    this.name = name;
    this.index = index;
    this.start_index = start_index;
    this.rows = rows;
    this.columns = columns;
  }
}
export const WorksheetSummary$WorksheetSummary = (name, index, start_index, rows, columns) =>
  new WorksheetSummary(name, index, start_index, rows, columns);
export const WorksheetSummary$isWorksheetSummary = (value) =>
  value instanceof WorksheetSummary;
export const WorksheetSummary$WorksheetSummary$name = (value) => value.name;
export const WorksheetSummary$WorksheetSummary$0 = (value) => value.name;
export const WorksheetSummary$WorksheetSummary$index = (value) => value.index;
export const WorksheetSummary$WorksheetSummary$1 = (value) => value.index;
export const WorksheetSummary$WorksheetSummary$start_index = (value) =>
  value.start_index;
export const WorksheetSummary$WorksheetSummary$2 = (value) => value.start_index;
export const WorksheetSummary$WorksheetSummary$rows = (value) => value.rows;
export const WorksheetSummary$WorksheetSummary$3 = (value) => value.rows;
export const WorksheetSummary$WorksheetSummary$columns = (value) =>
  value.columns;
export const WorksheetSummary$WorksheetSummary$4 = (value) => value.columns;

/**
 * `multiple_columns` can be present nth times in the columns.
 */
export const multiple_columns = /* @__PURE__ */ toList([
  /* @__PURE__ */ new AnswerColumn(),
  /* @__PURE__ */ new CategoryColumn(),
  /* @__PURE__ */ new ContextColumn(),
  /* @__PURE__ */ new MultipleChoiceColumn(),
  /* @__PURE__ */ new NumberingColumn(),
  /* @__PURE__ */ new UnknownColumn(),
]);

/**
 * `singleton_columns` can be present at most 1 time in the columns.
 */
export const singleton_columns = /* @__PURE__ */ toList([
  /* @__PURE__ */ new QuestionColumn(),
  /* @__PURE__ */ new TagColumn(),
  /* @__PURE__ */ new YesNoColumn(),
]);

/**
 * The worksheet as a `Worksheet`, whatever shape it came in. A
 * `LegacyWorksheet` predates cell mappings: it is given an empty one and no
 * detection yet, the exact shape a labelled sheet has before any label. A
 * `Worksheet` is returned as is.
 *
 * A V4 proposal never carries a `LegacyWorksheet` (`proposal.decoder`
 * promotes them on read, `proposal_builder.update_cell_mappings` never
 * produces one), so every reader of a V4 sheet can rely on `cell_mapping`.
 */
export function ensure_cell_mapping(worksheet) {
  if (worksheet instanceof Worksheet) {
    return worksheet;
  } else {
    let name = worksheet.name;
    let start_index = worksheet.start_index;
    let content = worksheet.content;
    let columns_type = worksheet.columns_type;
    let column_choices = worksheet.column_choices;
    let rows_type = worksheet.rows_type;
    let empty_columns = worksheet.empty_columns;
    let sheet_merged_cells = worksheet.sheet_merged_cells;
    let col_widths = worksheet.col_widths;
    let row_heights = worksheet.row_heights;
    let excel_style = worksheet.excel_style;
    return new Worksheet(
      name,
      start_index,
      content,
      columns_type,
      column_choices,
      rows_type,
      empty_columns,
      sheet_merged_cells,
      $dict.new$(),
      col_widths,
      row_heights,
      excel_style,
      new $remote.NotLoaded(),
    );
  }
}

export function encode_summary(summary) {
  return $json.object(
    toList([
      ["name", $json.string(summary.name)],
      ["index", $json.int(summary.index)],
      ["start_index", $json.int(summary.start_index)],
      ["rows", $json.int(summary.rows)],
      ["columns", $json.int(summary.columns)],
    ]),
  );
}

function encode_row_type(row_type) {
  if (row_type instanceof H1Row) {
    return $json.string("h1");
  } else if (row_type instanceof H2Row) {
    return $json.string("h2");
  } else if (row_type instanceof H3Row) {
    return $json.string("h3");
  } else if (row_type instanceof QuestionRow) {
    return $json.string("question");
  } else {
    return $json.string("other");
  }
}

export function encode_rows_type(rows) {
  let _pipe = rows;
  let _pipe$1 = $dict.to_list(_pipe);
  let _pipe$2 = $list.map(
    _pipe$1,
    (s) => { return [$int.to_string(s[0]), encode_row_type(s[1])]; },
  );
  return $json.object(_pipe$2);
}

export function encode_column_choices(column_choices) {
  let _pipe = column_choices;
  let _pipe$1 = $dict.to_list(_pipe);
  let _pipe$2 = $list.map(
    _pipe$1,
    (entry) => {
      let index = entry[0];
      let values = entry[1];
      return [$int.to_string(index), $json.array(values, $json.string)];
    },
  );
  return $json.object(_pipe$2);
}

function encode_column_type(column_type) {
  if (column_type instanceof ContextColumn) {
    return $json.string("context");
  } else if (column_type instanceof AnswerColumn) {
    return $json.string("answer");
  } else if (column_type instanceof CategoryColumn) {
    return $json.string("category");
  } else if (column_type instanceof MultipleChoiceColumn) {
    return $json.string("multiple_choice");
  } else if (column_type instanceof YesNoColumn) {
    return $json.string("yes_no");
  } else if (column_type instanceof NumberingColumn) {
    return $json.string("numbering");
  } else if (column_type instanceof QuestionColumn) {
    return $json.string("question");
  } else if (column_type instanceof TagColumn) {
    return $json.string("tag");
  } else {
    return $json.string("other");
  }
}

export function encode_columns_type(columns) {
  let _pipe = columns;
  let _pipe$1 = $dict.to_list(_pipe);
  let _pipe$2 = $list.map(
    _pipe$1,
    (s) => { return [$int.to_string(s[0]), encode_column_type(s[1])]; },
  );
  return $json.object(_pipe$2);
}

function encode_cell_merge_span(span) {
  return $json.object(
    toList([
      ["rowspan", $json.int(span.rowspan)],
      ["colspan", $json.int(span.colspan)],
    ]),
  );
}

function encode_merged_cells(sheet_merged_cells) {
  let _pipe = sheet_merged_cells;
  let _pipe$1 = $dict.to_list(_pipe);
  let _pipe$2 = $list.map(
    _pipe$1,
    (row_entry) => {
      let row = row_entry[0];
      let cols = row_entry[1];
      let _block;
      let _pipe$2 = cols;
      let _pipe$3 = $dict.to_list(_pipe$2);
      let _pipe$4 = $list.map(
        _pipe$3,
        (col_entry) => {
          let col = col_entry[0];
          let span = col_entry[1];
          return [$int.to_string(col), encode_cell_merge_span(span)];
        },
      );
      _block = $json.object(_pipe$4);
      let cols_json = _block;
      return [$int.to_string(row), cols_json];
    },
  );
  return $json.object(_pipe$2);
}

export function encode_coordinates(cell_coordinates) {
  return $json.object(
    toList([
      ["row", $json.int(cell_coordinates.row)],
      ["column", $json.int(cell_coordinates.column)],
    ]),
  );
}

export function encode_cell(cell) {
  return $json.object(
    toList([
      ["value", $json.nullable(cell.value, $json.string)],
      ["dropdown", $json.array(cell.dropdown, $json.string)],
      ["color", $json.int(cell.color)],
      ["inline_styles", $json.array(cell.inline_styles, $json.string)],
      [
        "original_coordinates",
        $json.nullable(cell.original_coordinates, encode_coordinates),
      ],
    ]),
  );
}

/**
 * Encodes a worksheet's cell mapping dict on its own, for callers that need
 * to send just the mapping (e.g. the post-import worksheet remap request)
 * rather than a full worksheet.
 */
export function encode_cell_mappings(cell_mapping_data) {
  let _pipe = cell_mapping_data;
  let _pipe$1 = $dict.to_list(_pipe);
  return $json.array(
    _pipe$1,
    (entry) => {
      let x;
      let y;
      let m;
      m = entry[1];
      x = entry[0][0];
      y = entry[0][1];
      return $json.object(
        toList([
          ["x", $json.int(x)],
          ["y", $json.int(y)],
          ["mapping", $cell_mapping.encode(m)],
        ]),
      );
    },
  );
}

export function encode_worksheet(worksheet) {
  let _block;
  if (worksheet instanceof Worksheet) {
    let cm = worksheet.cell_mapping;
    let col_widths = worksheet.col_widths;
    let row_heights = worksheet.row_heights;
    let excel_style = worksheet.excel_style;
    let advanced_detection = worksheet.advanced_detection;
    _block = [
      toList([
        ["cell_mapping", encode_cell_mappings(cm)],
        [
          "advanced_detection",
          $remote.encode(advanced_detection, $cell_graph.encode),
        ],
      ]),
      col_widths,
      row_heights,
      excel_style,
    ];
  } else {
    let col_widths = worksheet.col_widths;
    let row_heights = worksheet.row_heights;
    let excel_style = worksheet.excel_style;
    _block = [toList([]), col_widths, row_heights, excel_style];
  }
  let $ = _block;
  let cell_mapping_field = $[0];
  let col_widths = $[1];
  let row_heights = $[2];
  let excel_style = $[3];
  return $json.object(
    $list.append(
      toList([
        ["name", $json.string(worksheet.name)],
        ["start_index", $json.int(worksheet.start_index)],
        [
          "worksheet",
          $json.array(
            worksheet.content,
            (row) => {
              return $json.array(row, (cell) => { return encode_cell(cell); });
            },
          ),
        ],
        [
          "content",
          $json.array(
            worksheet.content,
            (row) => {
              return $json.array(row, (cell) => { return encode_cell(cell); });
            },
          ),
        ],
        [
          "sheet_merged_cells",
          encode_merged_cells(worksheet.sheet_merged_cells),
        ],
        ["columns_type", encode_columns_type(worksheet.columns_type)],
        ["column_choices", encode_column_choices(worksheet.column_choices)],
        ["rows_type", encode_rows_type(worksheet.rows_type)],
        ["empty_columns", $json.array(worksheet.empty_columns, $json.int)],
        ["col_widths", $json.array(col_widths, $json.int)],
        ["row_heights", $json.array(row_heights, $json.int)],
        ["excel_style", $json.bool(excel_style)],
      ]),
      cell_mapping_field,
    ),
  );
}

export function encode(spreadsheet) {
  return $json.object(
    toList([
      ["id", $json.string(spreadsheet.id)],
      ["name", $json.string(spreadsheet.name)],
      ["mime_type", $json.string($mime_types.to_string(spreadsheet.type_))],
      ["language", $json.nullable(spreadsheet.language, $iso639.encode)],
      [
        "data",
        $json.array(
          spreadsheet.data,
          (worksheet) => { return encode_worksheet(worksheet); },
        ),
      ],
      ["blob", $json.string($bit_array.base64_encode(spreadsheet.blob, false))],
      [
        "worksheets_key",
        $json.nullable(spreadsheet.worksheets_key, $json.string),
      ],
      [
        "worksheets_manifest",
        $json.array(
          spreadsheet.worksheets_manifest,
          (summary) => { return encode_summary(summary); },
        ),
      ],
    ]),
  );
}

/**
 * `index` is the worksheet's position in the proposal's worksheets, and is
 * what addresses its payload in S3 — names are unusable as keys because Excel
 * allows spaces, slashes and unicode in them.
 */
export function to_summary(worksheet, index) {
  let columns = $list.fold(
    worksheet.content,
    0,
    (widest, row) => { return $int.max(widest, $list.length(row)); },
  );
  return new WorksheetSummary(
    worksheet.name,
    index,
    worksheet.start_index,
    $list.length(worksheet.content),
    columns,
  );
}

export function summary_decoder() {
  return $decode.field(
    "name",
    $decode.string,
    (name) => {
      return $decode.field(
        "index",
        $decode.int,
        (index) => {
          return $decode.optional_field(
            "start_index",
            0,
            $decode.int,
            (start_index) => {
              return $decode.optional_field(
                "rows",
                0,
                $decode.int,
                (rows) => {
                  return $decode.optional_field(
                    "columns",
                    0,
                    $decode.int,
                    (columns) => {
                      return $decode.success(
                        new WorksheetSummary(
                          name,
                          index,
                          start_index,
                          rows,
                          columns,
                        ),
                      );
                    },
                  );
                },
              );
            },
          );
        },
      );
    },
  );
}

/**
 * Decodes the wire format produced by `encode_cell_mappings`, for callers
 * that receive a standalone cell mapping rather than a full worksheet.
 */
export function cell_mapping_dict_decoder() {
  let _pipe = $decode.list(
    $decode.field(
      "x",
      $decode.int,
      (x) => {
        return $decode.field(
          "y",
          $decode.int,
          (y) => {
            return $decode.field(
              "mapping",
              $cell_mapping.decoder(),
              (m) => { return $decode.success([[x, y], m]); },
            );
          },
        );
      },
    ),
  );
  return $decode.map(_pipe, $dict.from_list);
}

function cell_merge_span_decoder() {
  return $decode.field(
    "rowspan",
    $decode.int,
    (rowspan) => {
      return $decode.field(
        "colspan",
        $decode.int,
        (colspan) => {
          return $decode.success(new CellMergeSpan(rowspan, colspan));
        },
      );
    },
  );
}

function decode_cells_merge_dict() {
  return $decode.dict(
    (() => {
      let _pipe = $decode.string;
      let _pipe$1 = $decode.map(_pipe, $int.parse);
      return $decode.map(
        _pipe$1,
        (_capture) => { return $result.unwrap(_capture, 0); },
      );
    })(),
    $decode.dict(
      (() => {
        let _pipe = $decode.string;
        let _pipe$1 = $decode.map(_pipe, $int.parse);
        return $decode.map(
          _pipe$1,
          (_capture) => { return $result.unwrap(_capture, 0); },
        );
      })(),
      cell_merge_span_decoder(),
    ),
  );
}

export function parse_row_type(row_type) {
  if (row_type === "h1") {
    return new H1Row();
  } else if (row_type === "h2") {
    return new H2Row();
  } else if (row_type === "h3") {
    return new H3Row();
  } else if (row_type === "question") {
    return new QuestionRow();
  } else if (row_type === "sub-question") {
    return new QuestionRow();
  } else if (row_type === "section") {
    return new H1Row();
  } else {
    return new UnknownRow();
  }
}

export function type_decoder(parser) {
  let key = $decrypt.int();
  let content = $decode.map($decode.string, parser);
  return $decode.dict(key, content);
}

export function parse_column_type(column_type) {
  if (column_type === "category") {
    return new CategoryColumn();
  } else if (column_type === "context") {
    return new ContextColumn();
  } else if (column_type === "additional_information") {
    return new ContextColumn();
  } else if (column_type === "instructions") {
    return new ContextColumn();
  } else if (column_type === "answer") {
    return new AnswerColumn();
  } else if (column_type === "multiple_choice") {
    return new MultipleChoiceColumn();
  } else if (column_type === "question") {
    return new QuestionColumn();
  } else if (column_type === "tag") {
    return new TagColumn();
  } else if (column_type === "yes_no") {
    return new YesNoColumn();
  } else if (column_type === "numbering") {
    return new NumberingColumn();
  } else {
    return new UnknownColumn();
  }
}

function string_to_cell_decoder() {
  let _pipe = $decode.optional($decode.string);
  return $decode.map(
    _pipe,
    (_capture) => {
      return new Cell(_capture, new None(), toList([]), 0, toList([]));
    },
  );
}

function cell_decoder() {
  return $decode.field(
    "value",
    $decode.optional($decode.string),
    (value) => {
      return $decode.field(
        "dropdown",
        $decode.list(
          $decode.one_of(
            $decode.string,
            toList([
              $decode.map($decode.int, $int.to_string),
              $decode.map($decode.float, $float.to_string),
            ]),
          ),
        ),
        (dropdown) => {
          return $decrypt.optional_field(
            "color",
            $decode.int,
            (color) => {
              return $decrypt.optional_field(
                "inline_styles",
                $decode.list($decode.string),
                (inline_styles) => {
                  return $decrypt.optional_field(
                    "original_coordinates",
                    $decode.field(
                      "row",
                      $decode.int,
                      (row) => {
                        return $decode.field(
                          "column",
                          $decode.int,
                          (column) => {
                            return $decode.success(
                              new CellCoordinates(row, column),
                            );
                          },
                        );
                      },
                    ),
                    (original_coordinates) => {
                      return $decode.success(
                        (() => {
                          let inline_styles$1 = $option.unwrap(
                            inline_styles,
                            toList([]),
                          );
                          let color$1 = $option.unwrap(color, 0);
                          return new Cell(
                            value,
                            original_coordinates,
                            dropdown,
                            color$1,
                            inline_styles$1,
                          );
                        })(),
                      );
                    },
                  );
                },
              );
            },
          );
        },
      );
    },
  );
}

/**
 * Decodes the cell grid itself — rows of cells. Named for what it returns,
 * not for the worksheet it belongs to; `worksheet_decoder` is the whole
 * worksheet.
 * 
 * @ignore
 */
function cells_decoder() {
  let cell$1 = $decrypt.json(cell_decoder());
  let string_to_cell = string_to_cell_decoder();
  return $decode.list(
    $decode.list($decode.one_of(cell$1, toList([string_to_cell]))),
  );
}

/**
 * One worksheet. Public because worksheets are now also stored one object per
 * sheet in S3 (Pylon #1047), and read back individually.
 */
export function worksheet_decoder() {
  return $decode.field(
    "name",
    $decode.string,
    (name) => {
      return $decrypt.optional_field(
        "start_index",
        $decode.int,
        (start_index) => {
          let start_index$1 = $option.unwrap(start_index, 0);
          return $decode.then$(
            (() => {
              let decoder$1 = cells_decoder();
              let content = $decode.at(toList(["content"]), decoder$1);
              let worksheet = $decode.at(toList(["worksheet"]), decoder$1);
              return $decode.one_of(content, toList([worksheet]));
            })(),
            (content) => {
              return $decrypt.optional_field(
                "columns_type",
                type_decoder(parse_column_type),
                (columns_type) => {
                  let columns_type$1 = $option.lazy_unwrap(
                    columns_type,
                    $dict.new$,
                  );
                  return $decrypt.optional_field(
                    "column_choices",
                    $decode.dict($decrypt.int(), $decode.list($decode.string)),
                    (column_choices) => {
                      let column_choices$1 = $option.unwrap(
                        column_choices,
                        $dict.new$(),
                      );
                      return $decrypt.optional_field(
                        "rows_type",
                        type_decoder(parse_row_type),
                        (rows_type) => {
                          let rows_type$1 = $option.lazy_unwrap(
                            rows_type,
                            $dict.new$,
                          );
                          return $decrypt.optional_field(
                            "empty_columns",
                            $decode.list($decode.int),
                            (empty_columns) => {
                              let empty_columns$1 = $option.unwrap(
                                empty_columns,
                                toList([]),
                              );
                              return $decrypt.optional_field(
                                "sheet_merged_cells",
                                decode_cells_merge_dict(),
                                (sheet_merged_cells) => {
                                  let sheet_merged_cells$1 = $option.unwrap(
                                    sheet_merged_cells,
                                    $dict.new$(),
                                  );
                                  return $decrypt.optional_field(
                                    "col_widths",
                                    $decode.list($decode.int),
                                    (col_widths_opt) => {
                                      let col_widths = $option.unwrap(
                                        col_widths_opt,
                                        toList([]),
                                      );
                                      return $decrypt.optional_field(
                                        "row_heights",
                                        $decode.list($decode.int),
                                        (row_heights_opt) => {
                                          let row_heights = $option.unwrap(
                                            row_heights_opt,
                                            toList([]),
                                          );
                                          return $decrypt.optional_field(
                                            "excel_style",
                                            $decode.bool,
                                            (excel_style_opt) => {
                                              let excel_style = $option.unwrap(
                                                excel_style_opt,
                                                false,
                                              );
                                              return $decrypt.optional_field(
                                                "cell_mapping",
                                                cell_mapping_dict_decoder(),
                                                (cell_mapping_opt) => {
                                                  return $decrypt.optional_field(
                                                    "advanced_detection",
                                                    $remote.decoder(
                                                      $cell_graph.decoder(),
                                                    ),
                                                    (advanced_detection_opt) => {
                                                      let advanced_detection = $option.unwrap(
                                                        advanced_detection_opt,
                                                        new $remote.NotLoaded(),
                                                      );
                                                      if (
                                                        cell_mapping_opt instanceof Some
                                                      ) {
                                                        let cell_mapping = cell_mapping_opt[0];
                                                        return $decode.success(
                                                          new Worksheet(
                                                            name,
                                                            start_index$1,
                                                            content,
                                                            columns_type$1,
                                                            column_choices$1,
                                                            rows_type$1,
                                                            empty_columns$1,
                                                            sheet_merged_cells$1,
                                                            cell_mapping,
                                                            col_widths,
                                                            row_heights,
                                                            excel_style,
                                                            advanced_detection,
                                                          ),
                                                        );
                                                      } else {
                                                        return $decode.success(
                                                          new LegacyWorksheet(
                                                            name,
                                                            start_index$1,
                                                            content,
                                                            columns_type$1,
                                                            column_choices$1,
                                                            rows_type$1,
                                                            empty_columns$1,
                                                            sheet_merged_cells$1,
                                                            col_widths,
                                                            row_heights,
                                                            excel_style,
                                                          ),
                                                        );
                                                      }
                                                    },
                                                  );
                                                },
                                              );
                                            },
                                          );
                                        },
                                      );
                                    },
                                  );
                                },
                              );
                            },
                          );
                        },
                      );
                    },
                  );
                },
              );
            },
          );
        },
      );
    },
  );
}

export function data_decoder() {
  return $decode.list(worksheet_decoder());
}

export function decoder(blob) {
  return $decode.field(
    "id",
    $decode.string,
    (id) => {
      return $decode.field(
        "name",
        $decode.string,
        (name) => {
          return $decode.field(
            "mime_type",
            $mime_types.decoder(),
            (type_) => {
              return $decode.optional_field(
                "data",
                toList([]),
                data_decoder(),
                (data) => {
                  let iso639 = $decode.optional($iso639.decoder());
                  return $decode.optional_field(
                    "language",
                    new None(),
                    iso639,
                    (language) => {
                      return $decrypt.optional_field(
                        "worksheets_key",
                        $decode.string,
                        (worksheets_key) => {
                          return $decode.optional_field(
                            "worksheets_manifest",
                            toList([]),
                            $decode.list(summary_decoder()),
                            (worksheets_manifest) => {
                              return $decode.success(
                                new Spreadsheet(
                                  id,
                                  name,
                                  type_,
                                  data,
                                  language,
                                  blob,
                                  worksheets_key,
                                  worksheets_manifest,
                                ),
                              );
                            },
                          );
                        },
                      );
                    },
                  );
                },
              );
            },
          );
        },
      );
    },
  );
}

export function decode_row_type() {
  return (dyn) => {
    if (dyn === "h1") {
      return new Ok(new H1Row());
    } else if (dyn === "h2") {
      return new Ok(new H2Row());
    } else if (dyn === "h3") {
      return new Ok(new H3Row());
    } else if (dyn === "question") {
      return new Ok(new QuestionRow());
    } else if (dyn === "sub-question") {
      return new Ok(new QuestionRow());
    } else if (dyn === "section") {
      return new Ok(new H1Row());
    } else {
      return new Ok(new UnknownRow());
    }
  };
}

export function decode_column_type() {
  return (dyn) => {
    if (dyn === "category") {
      return new Ok(new CategoryColumn());
    } else if (dyn === "context") {
      return new Ok(new ContextColumn());
    } else if (dyn === "additional_information") {
      return new Ok(new ContextColumn());
    } else if (dyn === "instructions") {
      return new Ok(new ContextColumn());
    } else if (dyn === "answer") {
      return new Ok(new AnswerColumn());
    } else if (dyn === "multiple_choice") {
      return new Ok(new MultipleChoiceColumn());
    } else if (dyn === "question") {
      return new Ok(new QuestionColumn());
    } else if (dyn === "tag") {
      return new Ok(new TagColumn());
    } else if (dyn === "yes_no") {
      return new Ok(new YesNoColumn());
    } else if (dyn === "numbering") {
      return new Ok(new NumberingColumn());
    } else {
      return new Ok(new UnknownColumn());
    }
  };
}

/**
 * Access a cell in the worksheet, with coordinates #(x, y). As for every
 * cartesian system, you can use it directly as `x` and `y`, but you can
 * also use `column` and `row` labels to help manipulating the cell.
 *
 * ```gleam
 * let x = 0
 * let y = 0
 * cell(worksheet, x, y)
 *
 * let column = 0
 * let row = 0
 * cell(worksheet, column:, row:)
 * ```
 */
export function cell(worksheet, x, y) {
  let _pipe = worksheet.content;
  let _pipe$1 = $list_.at(_pipe, y);
  return $result.try$(_pipe$1, (_capture) => { return $list_.at(_capture, x); });
}

function do_collect_cells(worksheet, column, row, cells) {
  let $ = cell(worksheet, column, row);
  if ($ instanceof Ok) {
    let cell$1 = $[0];
    let _pipe = $dict.get(worksheet.rows_type, row);
    let _pipe$1 = $result.unwrap(_pipe, new UnknownRow());
    let _pipe$2 = ((_capture) => { return $pair.new$(cell$1, _capture); })(
      _pipe$1,
    );
    let _pipe$3 = ((_capture) => { return $list.prepend(cells, _capture); })(
      _pipe$2,
    );
    return ((_capture) => {
      return do_collect_cells(worksheet, column, row + 1, _capture);
    })(_pipe$3);
  } else {
    return $list.reverse(cells);
  }
}

function collect_cells(worksheet, column) {
  return do_collect_cells(worksheet, column, 0, toList([]));
}

export function select_questions_columns(worksheet) {
  let columns_type = $dict.to_list(worksheet.columns_type);
  return $list.flat_map(
    columns_type,
    (_use0) => {
      let column = _use0[0];
      let type_ = _use0[1];
      if (type_ instanceof ContextColumn) {
        return toList([]);
      } else if (type_ instanceof AnswerColumn) {
        return toList([]);
      } else if (type_ instanceof CategoryColumn) {
        return toList([]);
      } else if (type_ instanceof MultipleChoiceColumn) {
        return toList([]);
      } else if (type_ instanceof YesNoColumn) {
        return toList([]);
      } else if (type_ instanceof NumberingColumn) {
        return toList([]);
      } else if (type_ instanceof QuestionColumn) {
        return collect_cells(worksheet, column);
      } else if (type_ instanceof TagColumn) {
        return toList([]);
      } else {
        return toList([]);
      }
    },
  );
}

export function is_header(r) {
  if (r instanceof H1Row) {
    return true;
  } else if (r instanceof H2Row) {
    return true;
  } else if (r instanceof H3Row) {
    return true;
  } else if (r instanceof QuestionRow) {
    return false;
  } else {
    return false;
  }
}
